import { RateSource } from "@redotech/fx";
import { Money } from "@redotech/money";
import { ReactNode, useState } from "react";
import { copyToClipboard } from "../clipboard";
import { Tooltip } from "../tooltip/tooltip";
import { useFx } from "./fx-context";
import { resolveMoneyDisplay } from "./money-display";
import * as styles from "./money-text.module.css";
import { useWatchRate } from "./use-watch-rate";

export interface MoneyTextProps {
  /**
   * The amount to render; its own currency drives the conversion. A nullish
   * value renders nothing, so call sites can pass optional data directly.
   */
  value: Money | null | undefined;
  /** Source for live (unstruck) conversion. Defaults to OXR mid-market. */
  source?: RateSource;
  /** Color the figure by the value's sign — negative red, positive green. */
  signColor?: boolean;
  /**
   * Treat the figure as historical: never apply a live rate. If the value
   * carries a {@link Money.struck} snapshot it renders from that frozen rate;
   * otherwise it falls back to the value's own (foreign) currency as-is — no
   * live conversion, no secondary. Use for past amounts (completed orders,
   * settled transactions) where today's rate would misrepresent them.
   */
  struck?: boolean;
  /** Make the figure copy-on-click (restores copy lost vs static CopyableText). */
  copyable?: boolean;
  /** What `copyable` copies; defaults to the displayed figure. */
  copyValue?: string;
  className?: string;
}

/** {@link MoneyText} with a guaranteed value — the form the inner body renders. */
interface MoneyTextInnerProps extends Omit<MoneyTextProps, "value"> {
  value: Money;
}

function signClass(money: Money): string | undefined {
  const sign = Math.sign(money.amount);
  return sign < 0 ? styles.negative : sign > 0 ? styles.positive : undefined;
}

function cx(...names: (string | undefined)[]): string | undefined {
  return names.filter(Boolean).join(" ") || undefined;
}

/**
 * Renders a {@link Money} with the store currency (from {@link useFx}) as the
 * only figure — live-converted ("floating") or struck. The value's own currency
 * is not shown inline; it surfaces on hover via the rate tooltip. A nullish
 * value renders nothing — and skips the FX context + live-rate subscription
 * entirely, since the rendering hooks live in {@link MoneyTextInner}.
 */
export function MoneyText({ value, ...props }: MoneyTextProps) {
  if (!value) {
    return null;
  }
  return <MoneyTextInner value={value} {...props} />;
}

function MoneyTextInner({
  value,
  source = RateSource.Oxr,
  signColor = false,
  struck = false,
  copyable = false,
  copyValue,
  className,
}: MoneyTextInnerProps) {
  const { storeCurrency } = useFx();
  const [copied, setCopied] = useState(false);
  // Historical figures — a frozen snapshot, or caller-forced via `struck` —
  // never live-convert, so we skip the rate subscription.
  const historical = struck || value.struck != null;
  const { value: liveRate } = useWatchRate(
    !historical && value.currency !== storeCurrency
      ? { base: value.currency, quote: storeCurrency, source }
      : undefined,
  );
  const colorClass = signColor ? signClass(value) : undefined;
  const { primary, secondary, tooltip } = resolveMoneyDisplay(
    value,
    storeCurrency,
    liveRate,
    struck,
  );

  const copyString = copyValue ?? primary ?? secondary ?? undefined;
  const canCopy = copyable && copyString != null;
  const handleCopy = canCopy
    ? () => {
        void copyToClipboard(copyString);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    : undefined;

  const figureClass = cx(
    className,
    colorClass,
    tooltip || canCopy ? styles.explainable : undefined,
  );

  // Show only the store-currency figure (live or struck); the value's own
  // currency surfaces on hover via the tooltip. Fall back to the secondary
  // (native) figure only while a live rate is still pending (primary === null).
  let figure: ReactNode = (
    <span className={figureClass}>{primary ?? secondary}</span>
  );

  if (handleCopy) {
    figure = (
      <span
        className={styles.copyable}
        onClick={handleCopy}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleCopy();
          }
        }}
        role="button"
        tabIndex={0}
      >
        {figure}
      </span>
    );
  }

  // Preserve the rate explanation while signalling copy; show transient feedback.
  const tip = canCopy
    ? copied
      ? "Copied!"
      : tooltip
        ? `${tooltip} · Click to copy`
        : "Click to copy"
    : tooltip;

  // The shared Tooltip portals + tracks the anchor on scroll/resize, so no
  // manual positioning here.
  return tip ? <Tooltip title={tip}>{figure}</Tooltip> : figure;
}
